/**
 * BannerBar Component
 *
 * Horizontal banner strip rendered above or below the pane grid in
 * LiveTradingPhase. Displays configurable widgets (clock, daily objective,
 * economic events) in a compact single-row layout.
 *
 * - Orders enabled widgets by user-defined widgetOrder with registry fallback
 * - Looks up components from WIDGET_COMPONENT_MAP (component registry)
 * - Single widget: alignment-based justify (start/center/end)
 * - Multiple widgets: justify-between layout
 * - Empty-state hint when all widgets are disabled
 * - Mobile: horizontal scroll with snap for overflow
 * - Wrapped in React.memo for render isolation from pane-grid state
 *
 * @see constants/bannerWidget.ts — widget metadata registry
 * @see widgets/widgetComponentRegistry.ts — widget component mapping
 * @see hooks/useLayoutConfig.ts — ResolvedBannerConfig type
 */

import React, { useMemo } from 'react';

import { BANNER_WIDGETS } from '@/constants/bannerWidget';

import { WIDGET_COMPONENT_MAP } from './widgets/widgetComponentRegistry';

import type { BannerAlignment } from '@/types/shared';
import type { ClockWidgetConfig } from '@/types/shared/BannerWidget';
import type { ResolvedBannerConfig } from '@/hooks/useLayoutConfig';

// ===== CONSTANTS =====

/** Maps BannerAlignment values to Tailwind justify classes */
const ALIGNMENT_JUSTIFY_MAP: Record<BannerAlignment, string> = {
  start: 'justify-start',
  center: 'justify-center',
  end: 'justify-end',
};

// ===== TYPES =====

interface BannerBarProps {
  resolvedBannerConfig: ResolvedBannerConfig;
  onUpdateClockConfig?: (config: ClockWidgetConfig | undefined) => void;
}

// ===== COMPONENT =====

const BannerBarInner: React.FC<BannerBarProps> = ({ resolvedBannerConfig, onUpdateClockConfig }) => {
  const { widgets, widgetConfigs, widgetOrder, widgetAlignment } = resolvedBannerConfig;

  // Order enabled widgets by widgetOrder with registry fallback (research finding 6)
  const enabledWidgets = useMemo(() => {
    const enabled = BANNER_WIDGETS.filter((w) => widgets[w.id] ?? w.defaultEnabled);
    const enabledById = new Map(enabled.map((w) => [w.id, w]));

    const ordered: typeof enabled = [];
    // Ordered widgets first
    for (const id of widgetOrder) {
      const widget = enabledById.get(id);
      if (widget) {
        ordered.push(widget);
        enabledById.delete(id);
      }
    }
    // Fallback: remaining enabled widgets in registry order
    for (const w of enabled) {
      if (enabledById.has(w.id)) {
        ordered.push(w);
      }
    }
    return ordered;
  }, [widgets, widgetOrder]);

  // Dynamic justify: single widget uses alignment, multiple uses justify-between
  const justifyClass = enabledWidgets.length === 1
    ? ALIGNMENT_JUSTIFY_MAP[widgetAlignment]
    : 'justify-between';

  return (
    <div
      className="border-b border-divider bg-content1/50 px-3 py-1.5"
      role="region"
      aria-label="Banner bar"
    >
      {enabledWidgets.length > 0 ? (
        <div className={`flex items-center gap-4 overflow-x-auto flex-nowrap snap-x snap-mandatory scrollbar-hide ${justifyClass}`}>
          {enabledWidgets.map((widget) => {
            const WidgetComponent = WIDGET_COMPONENT_MAP[widget.id];
            return (
              <div key={widget.id} className="snap-start shrink-0">
                <WidgetComponent
                  widgetId={widget.id}
                  widgetConfigs={widgetConfigs}
                  {...(onUpdateClockConfig ? { onClockConfigChange: onUpdateClockConfig } : {})}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-default-400 text-center">
          Enable widgets from the Layout menu
        </p>
      )}
    </div>
  );
};

export const BannerBar = React.memo(BannerBarInner);
